import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getCampaignEvents,
  getCampaignLinks,
  getCampaignReport,
  getDeliverability,
  getReportExport,
  getReportOverview,
  type CampaignEventsQuery,
  type CampaignLinksQuery,
  type DeliverabilityQuery,
  type OverviewQuery,
} from '../services/analytics.service';
import type { ReportExportJob } from '../types/analytics-api.types';

const rangeReady = (from?: string, to?: string) => !from || !to || from <= to;

export function useReportOverview(query: OverviewQuery) {
  return useQuery({
    queryKey: ['reports', 'overview', query.from ?? '', query.to ?? '', query.interval ?? 'day'],
    queryFn: () => getReportOverview(query),
    enabled: rangeReady(query.from, query.to),
  });
}

export function useDeliverabilityReport(query: DeliverabilityQuery) {
  return useQuery({
    queryKey: [
      'reports',
      'deliverability',
      query.from ?? '',
      query.to ?? '',
      query.groupBy ?? 'domain',
      query.campaignId ?? '',
    ],
    queryFn: () => getDeliverability(query),
    enabled: rangeReady(query.from, query.to),
  });
}

export function useCampaignAnalyticsReport(campaignId: string, sendJobId?: string) {
  return useQuery({
    queryKey: ['reports', 'campaign', campaignId, sendJobId ?? ''],
    queryFn: () => getCampaignReport(campaignId, { sendJobId }),
    enabled: Boolean(campaignId),
  });
}

export function useCampaignEvents(
  campaignId: string,
  query: Omit<CampaignEventsQuery, 'cursor'>,
  enabled: boolean
) {
  return useInfiniteQuery({
    queryKey: [
      'reports',
      'events',
      campaignId,
      query.type ?? '',
      query.address ?? '',
      query.contactId ?? '',
      query.from ?? '',
      query.to ?? '',
      query.limit ?? 50,
    ],
    queryFn: ({ pageParam }) =>
      getCampaignEvents(campaignId, { ...query, cursor: pageParam || undefined }),
    initialPageParam: '' as string,
    getNextPageParam: (last) =>
      last.pageInfo.hasNext && last.pageInfo.nextCursor ? last.pageInfo.nextCursor : undefined,
    enabled: enabled && Boolean(campaignId) && rangeReady(query.from, query.to),
  });
}

export function useCampaignLinks(campaignId: string, query: CampaignLinksQuery) {
  return useQuery({
    queryKey: [
      'reports',
      'links',
      campaignId,
      query.deviceClass ?? '',
      query.from ?? '',
      query.to ?? '',
    ],
    queryFn: () => getCampaignLinks(campaignId, query),
    enabled: Boolean(campaignId) && rangeReady(query.from, query.to),
  });
}

export const EXPORT_POLL_MS = 3000;
/** A stuck worker leaves status at queued. Stop calling after this. */
export const EXPORT_POLL_WINDOW_MS = 2 * 60 * 1000;
const EXPORT_POLL_MAX_UPDATES = 40;

type ExportPollQuery = {
  state: {
    status: 'pending' | 'error' | 'success';
    dataUpdateCount: number;
    data?: Pick<ReportExportJob, 'status' | 'requestedAt'> | null;
  };
};

/**
 * Poll only while this view is following a job that is still queued or running,
 * and only for a short window. A restored export is checked once (`follow` false).
 */
export function exportJobRefetchInterval(query: ExportPollQuery, follow: boolean): number | false {
  if (!follow || query.state.status === 'error') return false;
  const job = query.state.data;
  if (!job || (job.status !== 'queued' && job.status !== 'in_progress')) return false;
  const requested = job.requestedAt ? Date.parse(job.requestedAt) : NaN;
  if (Number.isFinite(requested) && Date.now() - requested >= EXPORT_POLL_WINDOW_MS) return false;
  if (!Number.isFinite(requested) && query.state.dataUpdateCount >= EXPORT_POLL_MAX_UPDATES) {
    return false;
  }
  return EXPORT_POLL_MS;
}

export function useReportExportStatus(itemId: string | null, options?: { follow?: boolean }) {
  const follow = options?.follow ?? false;
  return useQuery({
    queryKey: ['reports', 'export', itemId],
    queryFn: () => {
      if (!itemId) throw new Error('Export id is required');
      return getReportExport(itemId);
    },
    enabled: Boolean(itemId),
    refetchInterval: (query) => exportJobRefetchInterval(query, follow),
  });
}
